import { useMemo } from 'react';
import '../../styles/BrigadePlaceWork.css';
import Table from '../Table/Table';
import MyMap from '../Maps/MyMap';
import { defaultCellRender } from '../../utils/formatValueForHTML';

const normalizeCoords = (coords) => {
  if (!coords || typeof coords !== 'object') {
    return null;
  }

  const rawLat = coords.lat;
  const rawLon = coords.lon;

  if (
    rawLat === null ||
    rawLat === undefined ||
    rawLon === null ||
    rawLon === undefined ||
    (typeof rawLat === 'string' && rawLat.trim() === '') ||
    (typeof rawLon === 'string' && rawLon.trim() === '')
  ) {
    return null;
  }

  const lat = Number(rawLat);
  const lon = Number(rawLon);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  return { lat, lon };
};

function BrigadePlaceWork({ currentSession, index, t }) {
  const renderCoordsCell = (value, color) => {
    const coords = normalizeCoords(value);

    if (!coords) {
      return '-';
    }

    return (
      <div style={{ color }}>
        <div>{defaultCellRender(coords.lat)}</div>
        <div>{defaultCellRender(coords.lon)}</div>
      </div>
    );
  };

  // Мемоизация списка маркеров
  const markers = useMemo(() => {
    const signal = currentSession?.signals?.[index];

    if (!signal) {
      return [];
    }

    const parsedMarkers = [];

    // Функция для создания маркера
    const createMarker = (coords, type, popupContent) => {
      const normalized = normalizeCoords(coords);

      if (!normalized) {
        return;
      }

      parsedMarkers.push({
        position: [normalized.lat, normalized.lon],
        type,
        popupContent,
      });
    };

    // Проверка и создание маркеров для каждого типа координат
    createMarker(signal?.iurCoords, 'green', t?.markerIur || 'ИУР');
    createMarker(signal?.kuobzCoords, 'blue', t?.markerKuobz || 'КУОБЗ');
    createMarker(signal?.uippsCoords, 'red', t?.markerUipps || 'УИППС');
    createMarker(signal?.uippsCoords1, 'red', t?.markerUipps || 'УИППС');
    createMarker(signal?.uippsCoords2, 'red', t?.markerUipps || 'УИППС');
    createMarker(signal?.uippsCoords3, 'red', t?.markerUipps || 'УИППС');
    createMarker(signal?.uippsCoords4, 'red', t?.markerUipps || 'УИППС');

    return parsedMarkers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession?.signals?.[index], t]);

  // Колонки таблицы
  const columns = useMemo(
    () => [
      {
        Header: t?.tableHeaderRoad || 'Дорога',
        accessor: 'road',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeaderStation || 'Станция/Перегон',
        accessor: 'station',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeaderOrd || 'Ордината места работ',
        accessor: 'ord',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeaderObject || 'Объект',
        accessor: 'obj',
        Cell: ({ value }) => <span className="text-nowrap">{defaultCellRender(value)}</span>,
      },
      {
        Header: t?.tableHeaderRail || 'РЦ',
        accessor: 'railName',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeadeAdjacentRails || 'Смежные РЦ',
        accessor: 'adjacentRails',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeaderWorkMode || 'Режим',
        accessor: 'workMode',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.tableHeaderWarningDistance || 'Расстояние оповещ.',
        accessor: 'warningDistance',
        Cell: ({ value }) => defaultCellRender(value),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSession, t]
  );

  const columnsTableCoords = useMemo(
    () => [
      {
        Header: () => <div style={{ color: '#33A74D' }}>{t?.iurCoordinates || 'Координаты ИУР'}</div>,
        accessor: 'iurCoords',
        Cell: ({ value }) => renderCoordsCell(value, '#33A74D'),
      },
      {
        Header: () => <div style={{ color: '#166EF1' }}>{t?.kuobzCoordinates || 'Координаты КУОБЗ'}</div>,
        accessor: 'kuobzCoords',
        Cell: ({ value }) => renderCoordsCell(value, '#166EF1'),
      },
      {
        Header: () => <div style={{ color: '#CA0000' }}>{`${t?.uippsCoordinates} 1` || 'Координаты УИППС 1'}</div>,
        accessor: 'uippsCoords',
        Cell: ({ value }) => renderCoordsCell(value, '#CA0000'),
      },
      {
        Header: () => <div style={{ color: '#CA0000' }}>{`${t?.uippsCoordinates} 2` || 'Координаты УИППС 2'}</div>,
        accessor: 'uippsCoords2',
        Cell: ({ value }) => renderCoordsCell(value, '#CA0000'),
      },
      {
        Header: () => <div style={{ color: '#CA0000' }}>{`${t?.uippsCoordinates} 3` || 'Координаты УИППС 3'}</div>,
        accessor: 'uippsCoords3',
        Cell: ({ value }) => renderCoordsCell(value, '#CA0000'),
      },

      {
        Header: () => <div style={{ color: '#CA0000' }}>{`${t?.uippsCoordinates} 4` || 'Координаты УИППС 4'}</div>,
        accessor: 'uippsCoords4',
        Cell: ({ value }) => renderCoordsCell(value, '#CA0000'),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSession?.signals, t]
  );

  return (
    <div className="brigade-place-work-table-container">
      {/* Таблица с данными о текущем месте работ */}
      <Table data={currentSession ? [currentSession] : []} columns={columns} hiddenDiv />
      {/*Таблица сигналов */}
      <Table
        className="customBorderRadius"
        data={currentSession?.signals?.[index] ? [currentSession?.signals?.[index]] : []}
        columns={columnsTableCoords}
        hiddenDiv
      />
      {/* Карта, требуется передавать координаты */}
      <div className="brigade-place-work-map-container">
        <MyMap markers={markers} t={t} />
      </div>
    </div>
  );
}

export default BrigadePlaceWork;
